import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight, Mail, Lock, User, Eye, EyeOff, ArrowLeft } from "lucide-react";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const claimToken = () => localStorage.getItem("pmw_claim") || undefined;

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { authenticate, setHasWedding } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const wantDemo = params.get("demo") === "1";

  useEffect(() => { if (wantDemo) localStorage.setItem("pmw_demo", "1"); }, [wantDemo]);

  const finish = async (data) => {
    const target = authenticate(data);
    if (localStorage.getItem("pmw_demo") && target === "/paywall") {
      try { await api.post("/demo"); setHasWedding(true); } catch (e) { console.error("demo load failed", e); }
      localStorage.removeItem("pmw_demo");
      localStorage.removeItem("pmw_claim");
      navigate("/app");
      return;
    }
    localStorage.removeItem("pmw_claim");
    navigate(target);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, claim_token: claimToken() });
      toast.success("Welcome back");
      await finish(data);
    } catch (e) {
      const msg = errText(e.response?.data?.detail);
      if (e.response?.status === 403 && /verify/i.test(msg)) { setMode("verify"); toast.message("Please verify your email — we sent a new code."); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitRegister = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", { email, password, name });
      toast.success("Check your email for a 6-digit code");
      setMode("verify");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code, claim_token: claimToken() });
      toast.success("Email verified");
      await finish(data);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-code", { email }); toast.success("New code sent"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const sendReset = async () => {
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); toast.success("If an account exists, a reset code was sent."); setMode("reset"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReset = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { email, code, new_password: password }); toast.success("Password reset — please log in."); setMode("login"); setPassword(""); setCode(""); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onKey = (fn) => (e) => { if (e.key === "Enter") fn(); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grain">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -right-16 -bottom-16 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground w-fit" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="relative">
          <div className="flex items-center gap-2 mb-6"><Heart className="h-6 w-6 text-accent" fill="currentColor" /><span className="tracking-[0.2em] text-sm">PLANMYWEDDING</span></div>
          <h1 className="text-5xl leading-tight tracking-tight">Your wedding,<br />beautifully planned.</h1>
          <p className="text-primary-foreground/70 mt-5 max-w-sm">Budget, guests, seating, vendors and your whole wedding day — saved to your account and ready on every device.</p>
        </div>
        <p className="relative text-xs text-primary-foreground/50">Your planner is private and secured to your account.</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8"><Heart className="h-6 w-6 text-accent" fill="currentColor" /><span className="tracking-[0.2em] text-sm text-primary">PLANMYWEDDING</span></div>

          {(mode === "login" || mode === "register") && (
            <>
              <h2 className="text-4xl tracking-tight text-primary">{mode === "login" ? "Welcome back." : "Create your account"}</h2>
              <p className="text-muted-foreground mt-2 mb-8">{mode === "login" ? "Your wedding plans are waiting." : "Let's start planning your beautiful day."}</p>

              <button onClick={googleLogin} className="w-full flex items-center justify-center gap-3 border border-border rounded-xl py-3 hover:bg-secondary transition-colors mb-5" data-testid="google-btn">
                <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5" />
                <span className="text-sm font-medium">Continue with Google</span>
              </button>
              <div className="flex items-center gap-3 my-5"><div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" /></div>

              {mode === "register" && (
                <div className="mb-4">
                  <Label className="text-xs text-muted-foreground">Your name</Label>
                  <div className="relative mt-1"><User className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" /><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" className="rounded-xl pl-9" data-testid="name-input" /></div>
                </div>
              )}
              <div className="mb-4">
                <Label className="text-xs text-muted-foreground">Email</Label>
                <div className="relative mt-1"><Mail className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" /><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl pl-9" data-testid="email-input" /></div>
              </div>
              <div className="mb-2">
                <Label className="text-xs text-muted-foreground">Password</Label>
                <div className="relative mt-1">
                  <Lock className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
                  <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey(mode === "login" ? submitLogin : submitRegister)} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} className="rounded-xl pl-9 pr-10" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-muted-foreground" tabIndex={-1}>{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
              {mode === "login" && <button onClick={() => setMode("forgot")} className="text-sm text-accent hover:underline mb-6 block" data-testid="forgot-link">Forgot password?</button>}
              {mode === "register" && <div className="mb-6" />}

              <Button onClick={mode === "login" ? submitLogin : submitRegister} disabled={busy || !email || !password} className="w-full rounded-full py-6 text-base" data-testid="submit-auth">
                {busy ? "Please wait…" : (mode === "login" ? "Log in" : "Continue")} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-6">
                {mode === "login" ? "New to PlanMyWedding? " : "Already have an account? "}
                <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-accent font-medium hover:underline" data-testid={mode === "login" ? "mode-register" : "mode-login"}>
                  {mode === "login" ? "Create an account" : "Log in"}
                </button>
              </p>
            </>
          )}

          {mode === "verify" && (
            <>
              <h2 className="text-4xl tracking-tight text-primary">Verify your email</h2>
              <p className="text-muted-foreground mt-2 mb-8">Enter the 6-digit code we sent to <b>{email}</b>. It expires in 10 minutes.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={onKey(submitVerify)} placeholder="000000" inputMode="numeric" className="rounded-xl text-center text-2xl tracking-[0.5em] py-6" data-testid="otp-input" />
              <Button onClick={submitVerify} disabled={busy || code.length !== 6} className="w-full rounded-full py-6 text-base mt-5" data-testid="verify-code">{busy ? "Verifying…" : "Verify & continue"}</Button>
              <div className="flex items-center justify-between mt-6 text-sm">
                <button onClick={() => setMode("register")} className="text-muted-foreground hover:underline">← Back</button>
                <button onClick={resend} className="text-accent hover:underline" data-testid="resend-code">Resend code</button>
              </div>
            </>
          )}

          {mode === "forgot" && (
            <>
              <h2 className="text-4xl tracking-tight text-primary">Reset password</h2>
              <p className="text-muted-foreground mt-2 mb-8">Enter your email and we'll send you a reset code.</p>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={onKey(sendReset)} className="rounded-xl mt-1 mb-5" data-testid="forgot-email" />
              <Button onClick={sendReset} disabled={busy || !email} className="w-full rounded-full py-6" data-testid="send-reset">Send reset code</Button>
              <button onClick={() => setMode("login")} className="text-sm text-muted-foreground hover:underline mt-6 block">← Back to login</button>
            </>
          )}

          {mode === "reset" && (
            <>
              <h2 className="text-4xl tracking-tight text-primary">Choose a new password</h2>
              <p className="text-muted-foreground mt-2 mb-8">Enter the code sent to <b>{email}</b> and your new password.</p>
              <Label className="text-xs text-muted-foreground">Reset code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="rounded-xl mt-1 mb-4 text-center tracking-[0.4em]" data-testid="reset-code" />
              <Label className="text-xs text-muted-foreground">New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey(submitReset)} className="rounded-xl mt-1 mb-5" data-testid="reset-password" />
              <Button onClick={submitReset} disabled={busy || code.length !== 6 || !password} className="w-full rounded-full py-6" data-testid="reset-submit">Reset password</Button>
              <button onClick={() => setMode("login")} className="text-sm text-muted-foreground hover:underline mt-6 block">← Back to login</button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
